import React, { useState, useEffect } from 'react';
import { getAgentConfig, updateAgentConfig } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Briefcase, CalendarClock, Plus, X, Save, Trash2, Clock, CheckCircle2,
  AlertTriangle, Target, ToggleLeft, ToggleRight
} from 'lucide-react';

const DAYS_PL = ['pon', 'wt', '\u015br', 'czw', 'pt', 'sob', 'ndz'];
const DAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const CATEGORIES = {
  negotiation: { pl: 'Negocjacje', en: 'Negotiation', color: 'bg-blue-500' },
  followup: { pl: 'Follow-upy', en: 'Follow-ups', color: 'bg-green-500' },
  research: { pl: 'Badania rynku', en: 'Research', color: 'bg-purple-500' },
  reporting: { pl: 'Raportowanie', en: 'Reporting', color: 'bg-orange-500' },
  other: { pl: 'Inne', en: 'Other', color: 'bg-gray-500' },
};

const PRIORITIES = {
  low: { pl: 'Niski', en: 'Low', color: 'bg-gray-400' },
  medium: { pl: '\u015aredni', en: 'Medium', color: 'bg-yellow-500' },
  high: { pl: 'Wysoki', en: 'High', color: 'bg-orange-500' },
  critical: { pl: 'Krytyczny', en: 'Critical', color: 'bg-red-500' },
};

function uuid() {
  return 'xxxxxxxx-xxxx-4xxx'.replace(/[x]/g, () => ((Math.random() * 16) | 0).toString(16));
}

export default function AgentRoles({ lang, tenantId }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roleDialog, setRoleDialog] = useState(false);
  const [taskDialog, setTaskDialog] = useState(false);
  const [newRole, setNewRole] = useState({ title: '', description: '', priority: 'medium' });
  const [newTask, setNewTask] = useState({
    title: '', description: '', type: 'recurring', category: 'negotiation',
    days: ['pon', 'wt', '\u015br', 'czw', 'pt'], time_from: '09:00', time_to: '17:00'
  });

  const days = lang === 'pl' ? DAYS_PL : DAYS_EN;

  useEffect(() => { loadConfig(); }, [tenantId]);

  async function loadConfig() {
    setLoading(true);
    try {
      const c = await getAgentConfig(tenantId);
      setConfig(c);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function handleSave(updatedConfig) {
    const c = updatedConfig || config;
    setSaving(true);
    try {
      const updated = await updateAgentConfig({
        identity: c.identity,
        beliefs: c.beliefs,
        roles: c.roles || [],
        tasks: c.tasks || [],
        language: c.language
      }, tenantId);
      setConfig(updated);
      toast.success(lang === 'pl' ? 'Zapisano' : 'Saved');
    } catch (err) {
      toast.error(lang === 'pl' ? 'B\u0142\u0105d zapisu' : 'Save error');
    }
    setSaving(false);
  }

  function addRole() {
    if (!newRole.title.trim()) {
      toast.error(lang === 'pl' ? 'Podaj nazw\u0119 roli' : 'Enter role name');
      return;
    }
    const role = {
      id: uuid(),
      ...newRole,
      active: true,
      created_at: new Date().toISOString()
    };
    const updated = { ...config, roles: [...(config.roles || []), role] };
    setConfig(updated);
    setRoleDialog(false);
    setNewRole({ title: '', description: '', priority: 'medium' });
    handleSave(updated);
  }

  function removeRole(roleId) {
    const updated = { ...config, roles: (config.roles || []).filter(r => r.id !== roleId) };
    setConfig(updated);
    handleSave(updated);
  }

  function toggleRole(roleId) {
    const updated = {
      ...config,
      roles: (config.roles || []).map(r =>
        r.id === roleId ? { ...r, active: !r.active } : r
      )
    };
    setConfig(updated);
    handleSave(updated);
  }

  function addTask() {
    if (!newTask.title.trim()) {
      toast.error(lang === 'pl' ? 'Podaj nazw\u0119 zadania' : 'Enter task name');
      return;
    }
    const task = {
      id: uuid(),
      ...newTask,
      active: true,
      created_at: new Date().toISOString()
    };
    const updated = { ...config, tasks: [...(config.tasks || []), task] };
    setConfig(updated);
    setTaskDialog(false);
    setNewTask({
      title: '', description: '', type: 'recurring', category: 'negotiation',
      days: ['pon', 'wt', '\u015br', 'czw', 'pt'], time_from: '09:00', time_to: '17:00'
    });
    handleSave(updated);
  }

  function removeTask(taskId) {
    const updated = { ...config, tasks: (config.tasks || []).filter(t => t.id !== taskId) };
    setConfig(updated);
    handleSave(updated);
  }

  function toggleTask(taskId) {
    const updated = {
      ...config,
      tasks: (config.tasks || []).map(t =>
        t.id === taskId ? { ...t, active: !t.active } : t
      )
    };
    setConfig(updated);
    handleSave(updated);
  }

  function toggleDay(day) {
    setNewTask(prev => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter(d => d !== day) : [...prev.days, day]
    }));
  }

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40 w-full" />)}</div>;
  }
  if (!config) return null;

  const roles = config.roles || [];
  const tasks = config.tasks || [];
  const activeRoles = roles.filter(r => r.active).length;
  const activeTasks = tasks.filter(t => t.active).length;

  return (
    <div className="space-y-6" data-testid="agent-roles-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {lang === 'pl' ? 'Role i Obowi\u0105zki' : 'Roles & Duties'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {lang === 'pl'
              ? `${activeRoles} aktywnych r\u00f3l \u2022 ${activeTasks} aktywnych zada\u0144`
              : `${activeRoles} active roles \u2022 ${activeTasks} active tasks`}
          </p>
        </div>
      </div>

      <Tabs defaultValue="roles" className="w-full">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="roles" className="gap-1.5">
            <Briefcase className="w-3.5 h-3.5" />
            {lang === 'pl' ? 'Role' : 'Roles'} ({roles.length})
          </TabsTrigger>
          <TabsTrigger value="tasks" className="gap-1.5">
            <CalendarClock className="w-3.5 h-3.5" />
            {lang === 'pl' ? 'Harmonogram zada\u0144' : 'Task Schedule'} ({tasks.length})
          </TabsTrigger>
        </TabsList>

        {/* Roles Tab */}
        <TabsContent value="roles" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={roleDialog} onOpenChange={setRoleDialog}>
              <DialogTrigger asChild>
                <Button className="gap-2" size="sm">
                  <Plus className="w-4 h-4" />
                  {lang === 'pl' ? 'Dodaj rol\u0119' : 'Add Role'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{lang === 'pl' ? 'Nowa rola' : 'New Role'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                  <div>
                    <Label className="text-xs">{lang === 'pl' ? 'Nazwa roli' : 'Role name'} *</Label>
                    <Input
                      value={newRole.title}
                      onChange={e => setNewRole(p => ({ ...p, title: e.target.value }))}
                      className="mt-1"
                      placeholder={lang === 'pl' ? 'np. Negocjator handlowy' : 'e.g. Sales Negotiator'}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{lang === 'pl' ? 'Opis obowi\u0105zk\u00f3w' : 'Description'}</Label>
                    <Textarea
                      value={newRole.description}
                      onChange={e => setNewRole(p => ({ ...p, description: e.target.value }))}
                      className="mt-1"
                      rows={3}
                      placeholder={lang === 'pl' ? 'Co agent robi w tej roli...' : 'What the agent does in this role...'}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{lang === 'pl' ? 'Priorytet' : 'Priority'}</Label>
                    <Select value={newRole.priority} onValueChange={v => setNewRole(p => ({ ...p, priority: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(PRIORITIES).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v[lang] || v.en}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={addRole} className="w-full">
                    {lang === 'pl' ? 'Dodaj rol\u0119' : 'Add Role'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {roles.length === 0 ? (
            <Card className="border border-dashed border-border">
              <CardContent className="py-12 text-center">
                <Briefcase className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">
                  {lang === 'pl' ? 'Brak przypisanych r\u00f3l. Dodaj pierwsz\u0105 rol\u0119 agentowi.' : 'No roles assigned. Add the first role to the agent.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {roles.map(role => (
                <Card key={role.id} className={`border transition-all ${role.active ? 'border-border' : 'border-border/30 opacity-60'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold">{role.title}</h3>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1">
                            <div className={`w-1.5 h-1.5 rounded-full ${PRIORITIES[role.priority]?.color || 'bg-gray-400'}`} />
                            {PRIORITIES[role.priority]?.[lang] || role.priority}
                          </Badge>
                        </div>
                        {role.description && (
                          <p className="text-xs text-muted-foreground mt-1">{role.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => toggleRole(role.id)}>
                          {role.active
                            ? <ToggleRight className="w-4 h-4 text-green-500" />
                            : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => removeRole(role.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tasks/Schedule Tab */}
        <TabsContent value="tasks" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={taskDialog} onOpenChange={setTaskDialog}>
              <DialogTrigger asChild>
                <Button className="gap-2" size="sm">
                  <Plus className="w-4 h-4" />
                  {lang === 'pl' ? 'Dodaj zadanie' : 'Add Task'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{lang === 'pl' ? 'Nowe zadanie' : 'New Task'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                  <div>
                    <Label className="text-xs">{lang === 'pl' ? 'Nazwa zadania' : 'Task name'} *</Label>
                    <Input
                      value={newTask.title}
                      onChange={e => setNewTask(p => ({ ...p, title: e.target.value }))}
                      className="mt-1"
                      placeholder={lang === 'pl' ? 'np. Przegląd nowych leadów' : 'e.g. Review new leads'}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{lang === 'pl' ? 'Opis' : 'Description'}</Label>
                    <Textarea
                      value={newTask.description}
                      onChange={e => setNewTask(p => ({ ...p, description: e.target.value }))}
                      className="mt-1" rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">{lang === 'pl' ? 'Typ' : 'Type'}</Label>
                      <Select value={newTask.type} onValueChange={v => setNewTask(p => ({ ...p, type: v }))}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="recurring">{lang === 'pl' ? 'Cykliczne' : 'Recurring'}</SelectItem>
                          <SelectItem value="one_time">{lang === 'pl' ? 'Jednorazowe' : 'One-time'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">{lang === 'pl' ? 'Kategoria' : 'Category'}</Label>
                      <Select value={newTask.category} onValueChange={v => setNewTask(p => ({ ...p, category: v }))}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(CATEGORIES).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v[lang] || v.en}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {newTask.type === 'recurring' && (
                    <>
                      <div>
                        <Label className="text-xs">{lang === 'pl' ? 'Dni tygodnia' : 'Days of week'}</Label>
                        <div className="flex gap-1.5 mt-1">
                          {days.map((day, i) => {
                            const dayKey = DAYS_PL[i];
                            const isActive = newTask.days.includes(dayKey);
                            return (
                              <button
                                key={day}
                                onClick={() => toggleDay(dayKey)}
                                className={`w-9 h-9 rounded-lg text-xs font-medium transition-colors ${
                                  isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                                }`}
                              >
                                {day}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">{lang === 'pl' ? 'Od godziny' : 'From'}</Label>
                          <Input type="time" value={newTask.time_from}
                            onChange={e => setNewTask(p => ({ ...p, time_from: e.target.value }))} className="mt-1" />
                        </div>
                        <div>
                          <Label className="text-xs">{lang === 'pl' ? 'Do godziny' : 'To'}</Label>
                          <Input type="time" value={newTask.time_to}
                            onChange={e => setNewTask(p => ({ ...p, time_to: e.target.value }))} className="mt-1" />
                        </div>
                      </div>
                    </>
                  )}

                  <Button onClick={addTask} className="w-full">
                    {lang === 'pl' ? 'Dodaj zadanie' : 'Add Task'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {tasks.length === 0 ? (
            <Card className="border border-dashed border-border">
              <CardContent className="py-12 text-center">
                <CalendarClock className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">
                  {lang === 'pl' ? 'Brak zaplanowanych zada\u0144. Dodaj harmonogram pracy agenta.' : 'No tasks scheduled. Add agent work schedule.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {/* Schedule overview */}
              <Card className="border border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs text-muted-foreground uppercase tracking-wider">
                    {lang === 'pl' ? 'Harmonogram tygodniowy' : 'Weekly Schedule'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-7 gap-1">
                    {days.map((day, i) => {
                      const dayKey = DAYS_PL[i];
                      const dayTasks = tasks.filter(t => t.active && t.type === 'recurring' && (t.days || []).includes(dayKey));
                      return (
                        <div key={day} className="text-center">
                          <p className="text-[10px] font-semibold text-muted-foreground mb-1">{day}</p>
                          <div className="space-y-0.5">
                            {dayTasks.map(t => (
                              <div
                                key={t.id}
                                className={`text-[9px] px-1 py-0.5 rounded ${CATEGORIES[t.category]?.color || 'bg-gray-500'} text-white truncate`}
                                title={`${t.title} (${t.time_from}-${t.time_to})`}
                              >
                                {t.time_from ? `${t.time_from}` : ''} {t.title.substring(0, 8)}
                              </div>
                            ))}
                            {dayTasks.length === 0 && (
                              <div className="text-[9px] text-muted-foreground/30 py-1">-</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Task list */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {tasks.map(task => (
                  <Card key={task.id} className={`border transition-all ${task.active ? 'border-border' : 'border-border/30 opacity-60'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${CATEGORIES[task.category]?.color || 'bg-gray-500'}`} />
                            <h3 className="text-sm font-semibold">{task.title}</h3>
                          </div>
                          {task.description && (
                            <p className="text-xs text-muted-foreground mt-1 ml-4">{task.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2 ml-4">
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                              {CATEGORIES[task.category]?.[lang] || task.category}
                            </Badge>
                            {task.type === 'recurring' && (
                              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {task.time_from && task.time_to ? `${task.time_from}-${task.time_to}` : ''}
                                {task.days?.length > 0 && ` (${task.days.join(', ')})`}
                              </span>
                            )}
                            {task.type === 'one_time' && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                {lang === 'pl' ? 'Jednorazowe' : 'One-time'}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => toggleTask(task.id)}>
                            {task.active
                              ? <ToggleRight className="w-4 h-4 text-green-500" />
                              : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                            onClick={() => removeTask(task.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
